import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  DatePickerUI,
} from '../../Interface';
import dayjs from 'dayjs';
import { Box, Tooltip } from '@mui/material';

function EditToolbar(props) {
  const { disabled, push, defSolution } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defSolution,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `solution.${values.solution.length ?? 0}.name`,
      `solution.${values.solution.length ?? 0}.responsiblePerson`,
      `solution.${values.solution.length ?? 0}.date`,
      `solution.${values.solution.length ?? 0}.status`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ComplaintReportD6 = ({ disabled, defSolution, setNoteDialogState }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.solution.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Intézkedés',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          const value = params?.row?.name ?? '';
          return (
            <Tooltip title={value}>
              <Box
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {value}
              </Box>
            </Tooltip>
          );
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`solution.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.solution?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`solution.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`solution.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'date',
        headerName: 'Dátum',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.date ? new Date(row.date) : '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`solution.${valueIndex}.date`}
              component={DatePickerUI}
              helperText={<FormErrorUI message={errors?.solution?.[valueIndex]?.date} />}
              variant="standard"
              value={dayjs(data.row.date)}
              onChange={(newDate) => {
                const finalDate = newDate ? newDate.startOf('day').add(12, 'hour').toDate() : null;

                setFieldValue(`solution.${valueIndex}.date`, finalDate);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: finalDate,
                });
              }}
              onBlur={async () => await validateField(`solution.${valueIndex}.date`)}
            />
          );
        },
      },
      {
        field: 'responsiblePerson',
        headerName: 'Felelős',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.solution?.[valueIndex]?.responsiblePerson;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`solution.${valueIndex}.responsiblePerson`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.solution?.[valueIndex]?.responsiblePerson} />}
              onChange={(e) => {
                setFieldValue(`solution.${valueIndex}.responsiblePerson`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`solution.${valueIndex}.responsiblePerson`)}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Állapot',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.solution?.[valueIndex]?.status;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`solution.${valueIndex}.status`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.solution?.[valueIndex]?.status} />}
              onChange={(e) => {
                setFieldValue(`solution.${valueIndex}.status`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`solution.${valueIndex}.status`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="solution">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ComplaintReportSolution}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.solution ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defSolution,
                  },
                }}
                onCellDoubleClick={(params, event) => {
                  event.defaultMuiPrevented = true;
                  if (disabled) return;
                  if (params.field === 'name') {
                    event.defaultMuiPrevented = true;
                    const valueIndex = getValueIndex(values, params.row.id);
                    setNoteDialogState({ open: true, fieldName: `solution.${valueIndex}.name` });
                    return;
                  }
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReportD6;
